import React, { useEffect, useRef, useState } from 'react'
import { logger } from '@/utils/logger'
import { Flame, Trophy } from 'lucide-react'
import { useIsSignedIn } from '@/hooks/useIsSignedIn'
import { useHotTipSettings } from '@/hooks/useHotTipSettings'

// Ticker flame for visitors who are not signed in: winners over $10
export const DEFAULT_FLAME_PRICE = 10

/**
 * Price above which a winner gets the flame. Signed-in users get their own Hot Tip price
 * (Dashboard; 0 = off, so no flames), like the flame on their tips (owner, 3 Nov 2026).
 */
export const flamePriceFor = (signedIn, hotTipMinPrice) =>
    signedIn ? Number(hotTipMinPrice) || 0 : DEFAULT_FLAME_PRICE

export const showsFlame = (odds, flamePrice) => flamePrice > 0 && odds > flamePrice

const CustomMarquee = ({ className = '' }) => {
    const signedIn = useIsSignedIn()
    const { hotTipMinPrice } = useHotTipSettings()
    const flamePrice = flamePriceFor(signedIn, hotTipMinPrice)
    const [winningTips, setWinningTips] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [isPaused, setIsPaused] = useState(false)
    const [stickyDate, setStickyDate] = useState('')
    const containerRef = useRef(null)
    const marqueeRef = useRef(null)
    const pausedAt = useRef(null)

    useEffect(() => {
        fetchWinningTips() // Initial fetch

        const intervalId = setInterval(() => {
            fetchWinningTips()
        }, 10 * 60 * 1000) // Refetch every 10 minutes

        return () => clearInterval(intervalId) // Cleanup on unmount
    }, [])

    // Sticky date tracking effect
    useEffect(() => {
        const checkStickyDate = () => {
            if (!containerRef.current || !marqueeRef.current || isPaused) return

            const container = containerRef.current
            const containerRect = container.getBoundingClientRect()
            const dateElements = marqueeRef.current.querySelectorAll('[data-date]')

            let newStickyDate = ''

            dateElements.forEach((element) => {
                const rect = element.getBoundingClientRect()
                const dateValue = element.getAttribute('data-date')

                // Check if this date element is at or crossing the left edge
                if (rect.left <= containerRect.left + 5 && rect.right > containerRect.left + 5) {
                    newStickyDate = dateValue || ''
                }
            })

            if (newStickyDate && newStickyDate !== stickyDate) {
                setStickyDate(newStickyDate)
            }
        }

        const interval = setInterval(checkStickyDate, 50)
        return () => clearInterval(interval)
    }, [stickyDate, isPaused])

    const fetchWinningTips = async () => {
        try {
            const response = await fetch('/api/winning-tips', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    minWinners: 10
                })
            })

            if (!response.ok) {
                throw new Error('Failed to fetch winning tips')
            }

            const data = await response.json()
            if (data.status === 'success' && data.data?.winningTips) {
                setWinningTips(data.data.winningTips)
            }
        } catch (err) {
            console.error('Error fetching winning tips:', err)
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    const handleMarqueeClick = () => {
        if (marqueeRef.current) {
            if (!isPaused) {
                // Pausing: get current transform and set as inline style
                const computedStyle = window.getComputedStyle(marqueeRef.current)
                const transform = computedStyle.getPropertyValue('transform')

                // Store the time when paused
                pausedAt.current = Date.now()

                // Apply current transform as inline style to freeze position
                marqueeRef.current.style.transform = transform
                marqueeRef.current.style.animation = 'none'
            } else {
                // Resuming: calculate elapsed time and adjust animation
                const currentTime = Date.now()
                const pauseDuration = pausedAt.current ? currentTime - pausedAt.current : 0

                // Get current transform to calculate position
                const computedStyle = window.getComputedStyle(marqueeRef.current)
                const transform = computedStyle.getPropertyValue('transform')

                // Extract translateX value from transform matrix
                let currentX = 0
                if (transform && transform !== 'none') {
                    const matrix = transform.match(/matrix.*\((.+)\)/)?.[1]?.split(', ')
                    if (matrix && matrix.length >= 6) {
                        currentX = parseFloat(matrix[4]) || 0
                    }
                }

                // Calculate the percentage of animation completed based on position
                // Assuming the animation moves from 0 to -100% (full width)
                const containerWidth = marqueeRef.current.parentElement?.offsetWidth || 0
                const contentWidth = marqueeRef.current.offsetWidth || 0

                if (contentWidth > 0) {
                    // Calculate how far we've moved as a percentage
                    const moveDistance = contentWidth / 3 // Since we have 3 copies
                    const progress = Math.abs(currentX) / moveDistance

                    // Create a new animation that starts from current position
                    marqueeRef.current.style.transform = `translateX(${currentX}px)`
                    marqueeRef.current.style.animation = 'none'

                    // Force reflow
                    marqueeRef.current.offsetHeight

                    // Resume animation with adjusted timing
                    const remainingProgress = 1 - (progress % 1)
                    const animationDuration = 120 // seconds (matches CSS animation duration)
                    const remainingDuration = remainingProgress * animationDuration

                    marqueeRef.current.style.animation = `marquee ${animationDuration}s linear infinite`
                    marqueeRef.current.style.animationDelay = `-${animationDuration - remainingDuration}s`
                } else {
                    // Fallback: just remove inline styles
                    marqueeRef.current.style.transform = ''
                    marqueeRef.current.style.animation = ''
                }
            }
        }
                 setIsPaused(!isPaused)
     }

     const formatDateToReadable = (dateString) => {
         // Handle case where dateString might already be a readable format like "TODAY", "YESTERDAY"
         if (!dateString) return 'UNKNOWN'
         
         // If it's already a readable format, return as is
         if (['TODAY', 'YESTERDAY', 'SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'].includes(dateString.toUpperCase())) {
             return dateString.toUpperCase()
         }

         // Try to parse the date string
         let inputDate
         try {
             // Handle different possible date formats
             if (dateString.includes('-')) {
                 // Format like "2024-01-15" or "15-01-2024"
                 inputDate = new Date(dateString)
             } else if (dateString.includes('/')) {
                 // Format like "01/15/2024" or "15/01/2024"
                 inputDate = new Date(dateString)
             } else {
                 // Try as is
                 inputDate = new Date(dateString)
             }

             // Check if date is valid
             if (isNaN(inputDate.getTime())) {
                 console.warn('Invalid date format:', dateString)
                 return dateString.toUpperCase() // Return original if can't parse
             }
         } catch (error) {
             console.warn('Error parsing date:', dateString, error)
             return dateString.toUpperCase() // Return original if can't parse
         }

         const today = new Date()
         const yesterday = new Date(today)
         yesterday.setDate(yesterday.getDate() - 1)

         // Reset time to compare only dates
         today.setHours(0, 0, 0, 0)
         yesterday.setHours(0, 0, 0, 0)
         inputDate.setHours(0, 0, 0, 0)

         if (inputDate.getTime() === today.getTime()) {
             return 'TODAY'
         } else if (inputDate.getTime() === yesterday.getTime()) {
             return 'YESTERDAY'
         } else {
             // For dates within the last week, show day name
             const daysDiff = Math.floor((today.getTime() - inputDate.getTime()) / (1000 * 60 * 60 * 24))
             if (daysDiff >= 0 && daysDiff <= 7) {
                 const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']
                 return dayNames[inputDate.getDay()]
             } else {
                 // For older dates, return a short date
                 try {
                     return inputDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                 } catch (error) {
                     console.warn('Error formatting date:', inputDate, error)
                     return dateString.toUpperCase() // Fallback to original
                 }
             }
         }
     }

     const formatWinningTipText = (tip, index) => {
         const trackName = tip.trackName
             .split(' ')
             .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
             .join(' ')

         const oddsValue = parseFloat(tip.odds)
         const hasValidOdds = !isNaN(oddsValue) && tip.odds !== 'N/A'

         return (
             <span key={`tip-${index}`} className="inline-flex items-center mr-8 whitespace-nowrap">
                 <span className="text-black font-semibold">
                     {trackName} R{tip.raceNumber}
                 </span>
                 <span className="text-brand-pink ml-1 font-bold">
                     #{tip.horseNumber} {tip.horseName}
                 </span>
                 {/* Pink lucide icons instead of the 🏆 / 🔥 emoji (owner, 2 Nov 2026: no tacky icons) */}
                 {hasValidOdds ? (
                     <span className="text-black font-light ml-1 inline-flex items-center gap-1">
                         <Trophy role="img" aria-label="Won" className="h-3.5 w-3.5 text-brand-pink" strokeWidth={2} />
                         ${oddsValue.toFixed(2)}
                         {showsFlame(oddsValue, flamePrice) && <Flame aria-hidden="true" data-testid="ticker-flame" className="h-3.5 w-3.5 text-brand-pink" strokeWidth={2} />}
                     </span>
                 ) : (
                     <span className="text-black font-light ml-1 inline-flex items-center">
                         <Trophy role="img" aria-label="Won" className="h-3.5 w-3.5 text-brand-pink" strokeWidth={2} />
                     </span>
                 )}
             </span>
         )
     }

     const groupTipsByDate = () => {
         const grouped = {}

         winningTips.forEach((tip, index) => {
             // Debug: log the first few date formats to see what we're working with
             //  if (index < 3) {
             //      logger.log('Date from API:', tip.date, 'Type:', typeof tip.date)
             //  }
             
             const readableDate = formatDateToReadable(tip.date)
             if (!grouped[readableDate]) {
                 grouped[readableDate] = []
             }
             grouped[readableDate].push(tip)
         })

         return grouped
     }

     const renderGroupedWinningTips = () => {
         const groupedTips = groupTipsByDate()
         const elements = []

         Object.entries(groupedTips).forEach(([date, tips]) => {
             // Add date header with data attribute for tracking
             elements.push(
                 <span
                     key={`date-${date}`}
                     data-date={date}
                     className={`text-[14px] font-bold text-brand-pink tracking-wide mr-6 whitespace-nowrap px-2 py-1 rounded bg-[#FFE6F6] ${
                         stickyDate === date ? 'opacity-0' : 'opacity-100'
                     }`}
                 >
                     {date}
                 </span>
             )

             // Add all tips for this date
             tips.forEach((tip, index) => {
                 elements.push(formatWinningTipText(tip, `${date}-${index}`))
             })
         })

         return elements
     }

     const marqueeContent = (
         <>
             <span className="text-[14px] font-bold text-brand-pink tracking-wide mr-8 whitespace-nowrap">
                 LEXI'S RECENT WINNERS
             </span>
             <div className="text-[12px] font-medium tracking-wide flex items-center">
                 {winningTips.length > 0 ? renderGroupedWinningTips() : (
                     <span className="text-black font-light">
                         No recent winners available
                     </span>
                 )}
             </div>
         </>
     )

    const containerClass = className
        ? `w-full ${className} flex items-center overflow-hidden cursor-pointer select-none relative`
        : 'w-full h-[40px] bg-[#FFE6F6] flex items-center overflow-hidden cursor-pointer select-none relative'

    const loadingClass = className
        ? `w-full ${className} flex items-center justify-center`
        : 'w-full h-[40px] bg-[#FFE6F6] flex items-center justify-center'

    if (loading || error) {
        return (
            <div className={loadingClass}>
                <span className="text-brand-pink text-sm"></span>
            </div>
        )
    }

         return (
         <div
             ref={containerRef}
             className={containerClass}
             onClick={handleMarqueeClick}
         >
             {/* Sticky date overlay */}
             {stickyDate && (
                 <div className="absolute left-0 top-0 h-full flex items-center z-20 pointer-events-none">
                     <span className="ticker-tape-day-fade text-[14px] font-bold text-brand-pink tracking-wide px-2 py-1 rounded">
                         {stickyDate}
                     </span>
                 </div>
             )}

             <div
                 ref={marqueeRef}
                 className={`flex animate-marquee`}
             >
                 {/* Multiple copies to ensure seamless loop */}
                 <div className='flex items-center whitespace-nowrap flex-shrink-0'>{marqueeContent}</div>
                 <div className='flex items-center whitespace-nowrap flex-shrink-0'>{marqueeContent}</div>
                 <div className='flex items-center whitespace-nowrap flex-shrink-0'>{marqueeContent}</div>
             </div>
         </div>
     )
}

export default CustomMarquee
